import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useTheme } from '@mui/material/styles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router';
import { PermissionKey } from '@c4ytechnology/authz';
import { AdminListPageHeader, adminDataTableSx, dataTableCardSx } from '../components/admin/adminListPageUi';
import { IamErrorAlert } from '../components/admin/iam/IamErrorAlert';
import { exportTruncatedMessage } from '../lib/adminCsvDownload';
import { StaffUserPicker, type StaffUserOption } from '../components/admin/iam/StaffUserPicker';
import {
  adminAccessReviewsApi,
  type AdminAccessReviewItem,
  type AdminReviewDecision,
} from '../api/adminConsole';
import { hasAdminPermission } from '../lib/adminPermissions';
import {
  canReassignItem,
  closeSummaryFromCounts,
  closeSummaryText,
  isReviewOverdue,
  isValidReassignTarget,
  itemDecidability,
  outcomeChip,
  reviewProgressLabel,
} from '../lib/accessReviews';
import { auditDeepLink } from '../lib/adminIamQuery';
import { formatIamTimestamp } from '../lib/iamAssignmentStatus';
import { useAdminConsoleMe } from '../hooks/useAdminConsoleMe';
import { ACCESS_REVIEWS_QUERY_KEY } from './AdminAccessReviews';

type DecisionFilter = 'all' | AdminReviewDecision | 'none';
const RISK_COLOR = { standard: 'default', sensitive: 'warning', critical: 'error' } as const;

/** One access review: items, keep/revoke decisions, reassignment, close and CSV evidence. */
export function AdminAccessReviewDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const theme = useTheme();
  const qc = useQueryClient();
  const me = useAdminConsoleMe().data;
  const canManage = hasAdminPermission(me, PermissionKey.IAM_REVIEWS_MANAGE);
  const canReadUsers = hasAdminPermission(me, PermissionKey.IAM_USERS_READ);
  const canReadAudit = hasAdminPermission(me, PermissionKey.ADMIN_AUDIT_READ);
  const [filter, setFilter] = useState<DecisionFilter>('all');
  const [decide, setDecide] = useState<{ item: AdminAccessReviewItem; decision: AdminReviewDecision } | null>(null);
  const [comment, setComment] = useState('');
  const [reassign, setReassign] = useState<AdminAccessReviewItem | null>(null);
  const [reviewer, setReviewer] = useState<StaffUserOption | null>(null);
  const [closing, setClosing] = useState(false);

  const detailKey = ['adminAccessReview', id] as const;
  const detailQ = useQuery({
    queryKey: [...detailKey, filter],
    queryFn: () => adminAccessReviewsApi.get(id, filter === 'all' ? undefined : filter),
    enabled: !!id,
    retry: false,
  });

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: detailKey });
    await qc.invalidateQueries({ queryKey: ACCESS_REVIEWS_QUERY_KEY });
  };

  const decideM = useMutation({
    mutationFn: (input: { itemId: string; decision: AdminReviewDecision; comment: string }) =>
      adminAccessReviewsApi.decide(id, input.itemId, {
        decision: input.decision,
        ...(input.comment.trim() ? { comment: input.comment.trim() } : {}),
      }),
    onSuccess: async () => {
      setDecide(null);
      await refresh();
    },
  });
  const reassignM = useMutation({
    mutationFn: (input: { itemId: string; reviewerUserId: string }) =>
      adminAccessReviewsApi.reassign(id, input.itemId, input.reviewerUserId),
    onSuccess: async () => {
      setReassign(null);
      await refresh();
    },
  });
  const closeM = useMutation({
    mutationFn: () => adminAccessReviewsApi.close(id),
    onSuccess: async () => {
      setClosing(false);
      setFilter('all');
      await refresh();
    },
  });
  const exportM = useMutation({ mutationFn: () => adminAccessReviewsApi.exportCsv(id) });

  const review = detailQ.data?.review;
  const items = detailQ.data?.items ?? [];
  const closed = review?.status === 'closed';
  const summary = review ? closeSummaryFromCounts(review) : null;

  const headerEnd = useMemo(
    () => (
      <Stack direction="row" gap={1}>
        {canReadAudit && id && (
          <Button component={RouterLink} to={auditDeepLink(id)} size="small">
            Audit
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={exportM.isPending ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon />}
          disabled={exportM.isPending}
          onClick={() => exportM.mutate()}
        >
          Export CSV
        </Button>
      </Stack>
    ),
    [canReadAudit, id, exportM],
  );

  if (detailQ.isPending && !detailQ.data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!review) {
    return (
      <Box sx={{ p: 2 }}>
        <Button component={RouterLink} to="/admin/access/reviews" startIcon={<ArrowBackRoundedIcon />} sx={{ mb: 2 }}>
          Access reviews
        </Button>
        <IamErrorAlert error={detailQ.error ?? new Error('Review not found')} />
      </Box>
    );
  }

  return (
    <Box>
      <AdminListPageHeader title={review.name} subtitle="Access review" endAdornment={headerEnd} />
      <Button component={RouterLink} to="/admin/access/reviews" startIcon={<ArrowBackRoundedIcon />} size="small" sx={{ mb: 2 }}>
        Access reviews
      </Button>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h6" sx={{ fontWeight: 700, mr: 1 }}>
              {review.name}
            </Typography>
            <Chip size="small" variant="outlined" color={closed ? 'default' : 'primary'} label={closed ? 'Closed' : 'Open'} />
            {isReviewOverdue(review) && <Chip size="small" variant="outlined" color="error" label="Overdue" />}
            <Chip size="small" variant="outlined" label={`Auto-revoke ${review.autoRevoke ? 'on' : 'off'}`} />
            <Box sx={{ flex: 1 }} />
            {canManage && !closed && (
              <Button
                variant="contained"
                color="warning"
                size="small"
                onClick={() => {
                  closeM.reset();
                  setClosing(true);
                }}
              >
                Close review
              </Button>
            )}
          </Stack>
          {review.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {review.description}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mt: 1 }}>
            Due {formatIamTimestamp(review.dueAt)} · {reviewProgressLabel(review.counts)} · {review.counts.keep} keep ·{' '}
            {review.counts.revoke} revoke · {review.counts.undecided} undecided
          </Typography>
          {closed && (
            <Typography variant="caption" color="text.secondary">
              Closed {formatIamTimestamp(review.closedAt)}. Outcomes are shown per item below.
            </Typography>
          )}
        </CardContent>
      </Card>

      <IamErrorAlert error={detailQ.error ?? exportM.error} sx={{ mb: 2 }} />
      {exportTruncatedMessage(exportM.data) && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => exportM.reset()}>
          {exportTruncatedMessage(exportM.data)}
        </Alert>
      )}

      <Tabs value={filter} onChange={(_, v: DecisionFilter) => setFilter(v)} sx={{ mb: 1 }}>
        <Tab value="all" label="All" />
        <Tab value="none" label="Undecided" />
        <Tab value="keep" label="Keep" />
        <Tab value="revoke" label="Revoke" />
      </Tabs>

      <Card variant="outlined" sx={dataTableCardSx(theme)}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={adminDataTableSx(theme)}>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Last login</TableCell>
                <TableCell>Reviewer</TableCell>
                <TableCell>Decision</TableCell>
                {closed && <TableCell>Outcome</TableCell>}
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const can = itemDecidability(me, review, item);
                const outcome = outcomeChip(item.outcome);
                return (
                  <TableRow key={item.id}>
                    <TableCell>
                      {canReadUsers ? (
                        <RouterLink to={`/admin/users/${encodeURIComponent(item.userId)}`}>{item.userEmail}</RouterLink>
                      ) : (
                        item.userEmail
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <span>{item.roleName}</span>
                        {item.roleRisk && (
                          <Chip size="small" variant="outlined" color={RISK_COLOR[item.roleRisk] ?? 'default'} label={item.roleRisk} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {item.userLastLoginAt ? formatIamTimestamp(item.userLastLoginAt) : 'Never'}
                    </TableCell>
                    <TableCell>{item.reviewerEmail ?? <Typography variant="caption" color="text.secondary">Unassigned</Typography>}</TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      {item.decision ? (
                        <>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={item.decision === 'keep' ? 'success' : 'error'}
                            label={item.decision === 'keep' ? 'Keep' : 'Revoke'}
                          />
                          {item.comment && (
                            <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                              {item.comment}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {item.decidedBy ?? '—'} · {formatIamTimestamp(item.decidedAt)}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Undecided
                        </Typography>
                      )}
                    </TableCell>
                    {closed && (
                      <TableCell>
                        {outcome ? <Chip size="small" variant="outlined" color={outcome.color} label={outcome.label} /> : '—'}
                        {item.outcomeDetail && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {item.outcomeDetail}
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {!closed && (
                        <Tooltip title={can.allowed ? '' : can.reason}>
                          <span>
                            <Button
                              size="small"
                              color="success"
                              disabled={!can.allowed}
                              onClick={() => {
                                decideM.reset();
                                setComment(item.comment ?? '');
                                setDecide({ item, decision: 'keep' });
                              }}
                            >
                              Keep
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              disabled={!can.allowed}
                              onClick={() => {
                                decideM.reset();
                                setComment(item.comment ?? '');
                                setDecide({ item, decision: 'revoke' });
                              }}
                            >
                              Revoke
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {canReassignItem(me, review) && (
                        <Button
                          size="small"
                          onClick={() => {
                            reassignM.reset();
                            setReviewer(null);
                            setReassign(item);
                          }}
                        >
                          Reassign
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={closed ? 7 : 6}>
                    <Typography variant="body2" color="text.secondary">
                      No items in this view.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Card>

      <Dialog open={Boolean(decide)} onClose={() => setDecide(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{decide?.decision === 'keep' ? 'Keep access' : 'Revoke access'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <IamErrorAlert error={decideM.error} />
            <Typography variant="body2">
              <strong>{decide?.item.roleName}</strong> for {decide?.item.userEmail}
            </Typography>
            {decide?.decision === 'revoke' && (
              <Alert severity="warning">The role is removed when the review is closed, not now.</Alert>
            )}
            <TextField
              label="Comment (optional)"
              size="small"
              multiline
              minRows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecide(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={decide?.decision === 'keep' ? 'success' : 'error'}
            disabled={!decide || decideM.isPending}
            onClick={() => decide && decideM.mutate({ itemId: decide.item.id, decision: decide.decision, comment })}
          >
            {decide?.decision === 'keep' ? 'Keep' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(reassign)} onClose={() => setReassign(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reassign reviewer</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <IamErrorAlert error={reassignM.error} />
            <Typography variant="body2">
              {reassign?.roleName} for {reassign?.userEmail}
            </Typography>
            <StaffUserPicker
              label="New reviewer"
              value={reviewer}
              onChange={setReviewer}
              excludeIds={reassign ? [reassign.userId] : []}
              helperText="The user under review cannot review themselves."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReassign(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!reassign || !reviewer || !isValidReassignTarget(reassign, reviewer.id) || reassignM.isPending}
            onClick={() => reassign && reviewer && reassignM.mutate({ itemId: reassign.id, reviewerUserId: reviewer.id })}
          >
            Reassign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={closing} onClose={() => setClosing(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Close “{review.name}”?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <IamErrorAlert error={closeM.error} />
            {summary && (
              <>
                <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
                  <Chip color="error" variant="outlined" label={`${summary.willRevoke} will be revoked`} />
                  <Chip color="success" variant="outlined" label={`${summary.keep} kept`} />
                  <Chip color="warning" variant="outlined" label={`${summary.undecided} undecided`} />
                </Stack>
                <Typography variant="body2">{closeSummaryText(summary, review.autoRevoke)}</Typography>
              </>
            )}
            <Typography variant="caption" color="text.secondary">
              Closing cannot be undone. Decisions are locked afterwards.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClosing(false)}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={closeM.isPending} onClick={() => closeM.mutate()}>
            Close review
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
